import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Calendar, Users, Cat, Receipt, Settings, LogOut, Euro, ClipboardList, X, Activity, BookOpen, AlertTriangle, ArrowLeftCircle, Bell, Image, Dumbbell } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import axios from 'axios';
import { APP_VERSION } from '../version';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Sidebar = ({ onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isInstructor, isAdmin, login } = useAuth();
  const [clubName, setClubName] = useState('');
  const [licenceWarning, setLicenceWarning] = useState(null);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API_URL}/api/club/info`)
      .then(res => setClubName(res.data?.nom || ''))
      .catch(() => {}); // silencieux — l'utilisateur est authentifié, pas de fallback séquentiel
  }, [user]);

  // Check licence status for admins
  useEffect(() => {
    const fetchLicenceStatus = async () => {
      if (!isAdmin()) return;
      try {
        const res = await axios.get(`${API_URL}/api/club/licence-status`);
        const data = res.data;
        if (data.days_until_expiration !== null && data.days_until_expiration <= 30) {
          setLicenceWarning({
            days: data.days_until_expiration,
            status: data.status
          });
        }
      } catch (error) {
        console.error('Error fetching licence status:', error);
      }
    };
    fetchLicenceStatus();
  }, [isAdmin]);

  // Check if user is pending approval
  const isPendingApproval = user?.is_approved === 'en_attente';
  
  // Check if user has access to horses (instructor, owner, PP, or DP)
  const hasHorseAccess = () => {
    if (isInstructor()) return true;
    const role = user?.role_club || '';
    return ['cavalier_proprietaire', 'cavalier_proprio', 'cavalier_pp', 'cavalier_dp'].includes(role);
  };
  
  // Check if super admin is impersonating a club
  const isImpersonating = user?.impersonating === true && user?.system_role === 'super_admin';

  const handleExitImpersonation = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/superadmin/clubs/exit-impersonation`);
      
      // Store the new token and user data
      localStorage.setItem('equibook_token', response.data.token);
      // Clear impersonating user data to force refresh from API
      localStorage.removeItem('equibook_impersonating_user');
      
      // Force full page reload to ensure state is properly updated
      window.location.href = '/superadmin';
    } catch (error) {
      console.error('Error exiting impersonation:', error);
    }
  };

  // Limited menu for pending users
  const pendingNavItems = [
    { icon: Euro, label: 'Tarifs', path: '/billing?tab=tarifs' },
    { icon: BookOpen, label: 'Guide', path: '/guide' },
    { icon: Settings, label: 'Paramètres', path: '/settings' },
  ];

  // Full menu for approved users
  const fullNavItems = [
    { icon: Home, label: 'Dashboard', path: '/dashboard' },
    { icon: Calendar, label: 'Planning', path: '/planning' },
    ...(isInstructor() ? [
      { icon: Users, label: 'Cavaliers', path: '/users' },
    ] : []),
    // Horses menu - accessible to instructors, owners, PP, and DP
    ...(hasHorseAccess() ? [
      { icon: Cat, label: 'Chevaux', path: '/horses' },
    ] : []),
    ...(isInstructor() ? [
      { icon: Activity, label: 'Suivi Chevaux', path: '/horses/dashboard' },
      { icon: ClipboardList, label: 'Groupes', path: '/groups' },
    ] : []),
   // { icon: Image, label: 'Galerie', path: '/gallery' },
    { icon: Dumbbell, label: 'Dresstek', path: '/dresstek' },
    { icon: Receipt, label: 'Tarifs et facturation', path: '/billing' },
    ...(isAdmin() ? [
      { icon: Bell, label: 'Notifications', path: '/notifications/admin' },
    ] : []),
    { icon: BookOpen, label: 'Guide', path: '/guide' },
    { icon: Settings, label: 'Paramètres', path: '/settings' },
  ];

  const navItems = isPendingApproval ? pendingNavItems : fullNavItems;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (onClose) onClose();
  };

  return (
    <div className="flex flex-col h-full bg-[#213418] text-white" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
      {/* Logo */}
      <div className="p-6 border-b border-white/10 flex items-center justify-between">
        <div 
          onClick={() => navigate('/dashboard')}
          className="cursor-pointer hover:opacity-80 transition-opacity"
        >
          <div className="flex items-baseline gap-2">
            <h1 className="text-2xl font-serif font-bold text-white">Equibook</h1>
            <span className="text-xs text-white/40">v{APP_VERSION}</span>
          </div>
          {clubName && <p className="text-sm text-white/60 mt-1">{clubName}</p>}
        </div>
        {/* Close button for mobile */}
        {onClose && (
          <button 
            onClick={onClose}
            className="md:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Fermer le menu"
          >
            <X size={24} />
          </button>
        )}
      </div>
      
      {/* Super Admin Impersonation Banner */}
      {isImpersonating && (
        <div className="mx-4 mt-4 p-3 rounded-lg bg-purple-500/20 border border-purple-500/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-purple-300">Mode Super Admin</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleExitImpersonation}
              className="text-purple-300 hover:text-white hover:bg-purple-500/30 h-7 px-2"
              data-testid="exit-impersonation-btn"
            >
              <ArrowLeftCircle className="w-4 h-4 mr-1" />
              Retour
            </Button>
          </div>
        </div>
      )}
      
      {/* Licence Warning for Admins */}
      {licenceWarning && (
        <div className={`mx-4 mt-4 p-3 rounded-lg ${licenceWarning.days <= 7 ? 'bg-red-500/20 border border-red-500/50' : 'bg-yellow-500/20 border border-yellow-500/50'}`}>
          <div className="flex items-center gap-2">
            <AlertTriangle className={`w-4 h-4 ${licenceWarning.days <= 7 ? 'text-red-400' : 'text-yellow-400'}`} />
            <span className="text-sm font-medium">
              {licenceWarning.days <= 0 ? 'Licence expirée !' : `Licence expire dans ${licenceWarning.days} jours`}
            </span>
          </div>
          {licenceWarning.status !== 'actif' && (
            <p className="text-xs text-white/70 mt-1">
              Contactez Equibook pour renouveler
            </p>
          )}
        </div>
      )}
      
      {/* Navigation */}
      <nav className="flex-1 px-4 py-2 space-y-0.5 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.path}
              onClick={() => handleNavigate(item.path)}
              data-testid={`sidebar-${item.label.toLowerCase()}`}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${
                isActive
                  ? 'bg-white/20 text-white'
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
              }`}
            >
              <Icon size={18} strokeWidth={1.5} />
              <span className="text-sm font-medium">{item.label}</span>
            </button>
          );
        })}
      </nav>
      
      {/* User section */}
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <span className="text-sm font-medium">
              {user?.prenom?.[0]}{user?.nom?.[0]}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">
              {user?.prenom} {user?.nom}
            </p>
            <p className="text-xs text-white/60 truncate">
              {user?.role_club?.replace('_', ' ')}
            </p>
          </div>
        </div>
        
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-3 px-4 py-3 mt-2 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-all"
        >
          <LogOut size={20} strokeWidth={1.5} />
          <span className="font-medium">Déconnexion</span>
        </button>
      </div>
    </div>
  );
};
